// ID1 (docs/build-briefs/ID1-id-verified-ticket-action.md; PRD P0.10; design/mockups/id1/id1-4-location-settings-toggle.html).
// ID1-F2 (DEC-398; design/mockups/id1-f2/id1-f2-1-settings-three-way.html): the switch is now a three-way, Off / Optional /
// Required. Settings > Location > Service: the location's mode for the ID Verified action on a ticket with alcohol. Off by default.
//
// Over GET and PUT /tenants/:tenantId/locations/:locationId/id-verification. The location is chosen from the locations the server
// lists (this device's location first), never typed. The switch acts over its own call and is NOT part of any Save. After the
// call it reads what the server holds and draws that. Visible to a session that may view settings; the switch is off for
// one that may not manage them (resolved permissions, never a role name). Admin surface: keyboard and pointer are fine,
// every control is 44px or taller. All text via textContent.

import {
  getIdVerificationSetting,
  listTenantLocations,
  putIdVerificationSetting,
  type IdVerificationModeKey,
  type TenantLocationNameDto,
} from "./api.ts";
import { sortByName } from "./nameSort.ts";

export interface IdVerifySettingDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.tenant_settings.view (the read of this section). */
  canView: () => boolean;
  /** The session holds admin.location_settings.manage (the save). */
  canManage: () => boolean;
  /** The location this device belongs to, when it is set up as one. */
  deviceLocation: () => { id: string; name: string } | undefined;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
}

export interface IdVerifySettingSection {
  refresh(): Promise<void>;
  reset(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

const ID_PREFIX = "admin-id-verify";

export function mountIdVerifySetting(
  root: HTMLElement,
  deps: IdVerifySettingDeps,
): IdVerifySettingSection {
  let locations: { id: string; name: string }[] = [];
  let locationId: string | undefined;
  let mode: IdVerificationModeKey | undefined;
  let modes: { key: IdVerificationModeKey; label: string }[] = [];
  let errorLine = "";
  let saving = false;
  let loadSequence = 0;
  root.hidden = true;

  function reset(): void {
    loadSequence += 1;
    locations = [];
    locationId = undefined;
    mode = undefined;
    modes = [];
    errorLine = "";
    saving = false;
    root.replaceChildren();
    root.hidden = true;
  }

  async function loadLocations(): Promise<void> {
    const listed = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
    const device = deps.deviceLocation();
    if (listed.status === 200 && Array.isArray(listed.body.locations)) {
      const found = listed.body.locations as TenantLocationNameDto[];
      locations = found.map((siteLocation) => ({ id: siteLocation.id, name: siteLocation.name }));
      const preferred =
        (device !== undefined &&
          locations.find((siteLocation) => siteLocation.id === device.id)?.id) ||
        found.find((siteLocation) => siteLocation.isMain)?.id ||
        locations[0]?.id;
      locationId =
        locationId !== undefined && locations.some((siteLocation) => siteLocation.id === locationId)
          ? locationId
          : preferred;
      return;
    }
    locations = device === undefined ? [] : [device];
    locationId = device?.id;
  }

  async function load(): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    const sequence = (loadSequence += 1);
    if (locations.length === 0) {
      await loadLocations();
      if (sequence !== loadSequence || !deps.isCurrent()) return;
    }
    if (locationId === undefined) {
      reset();
      return;
    }
    const result = await getIdVerificationSetting(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status === 200 && typeof result.body.mode === "string") {
      mode = result.body.mode;
      modes = result.body.modes;
      render();
      return;
    }
    if (result.status === 403 || result.status === 404) {
      reset();
      return;
    }
    mode = undefined;
    errorLine = `Could not load ID Verified. ${messageOf(result.body)}`;
    render();
  }

  function render(): void {
    root.replaceChildren();
    root.hidden = false;
    const card = node("section", "bd-card");
    card.dataset["testid"] = `${ID_PREFIX}-card`;
    const head = node("div", "bd-card-head");
    head.append(node("h3", "bd-card-title", "Service"));
    card.append(head);

    if (locations.length > 1) {
      const chips = node("div", "rt-locations");
      chips.setAttribute("role", "group");
      chips.setAttribute("aria-label", "Which location");
      for (const siteLocation of sortByName(locations, (listed) => listed.name)) {
        const chip = node("button", "tablets-chip", siteLocation.name);
        chip.type = "button";
        chip.dataset["testid"] = `${ID_PREFIX}-location`;
        chip.dataset["locationId"] = siteLocation.id;
        chip.setAttribute("aria-pressed", String(siteLocation.id === locationId));
        chip.addEventListener("click", () => {
          if (siteLocation.id === locationId || saving) return;
          locationId = siteLocation.id;
          errorLine = "";
          void load();
        });
        chips.append(chip);
      }
      card.append(chips);
    }

    if (mode !== undefined) {
      const row = node("div", "svc-row");
      const text = node("div", "svc-text");
      text.append(
        node("span", "svc-title", "ID Verified"),
        node("span", "svc-sub", "Tickets with alcohol"),
      );
      const seg = node("div", "svc-seg");
      seg.setAttribute("role", "radiogroup");
      seg.setAttribute("aria-label", "ID Verified on tickets with alcohol");
      seg.dataset["testid"] = `${ID_PREFIX}-mode`;
      for (const option of modes) {
        const button = node("button", "svc-seg-btn", option.label);
        button.type = "button";
        button.setAttribute("role", "radio");
        button.setAttribute("aria-checked", String(option.key === mode));
        button.dataset["testid"] = `${ID_PREFIX}-mode-${option.key}`;
        button.disabled = !deps.canManage() || saving;
        button.addEventListener("click", () => {
          if (option.key !== mode) void save(option.key);
        });
        seg.append(button);
      }
      row.append(text, seg);
      card.append(row);
    }
    if (errorLine !== "") {
      const error = node("p", "admin-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = `${ID_PREFIX}-error`;
      card.append(error);
    }
    root.append(card);
  }

  async function save(next: IdVerificationModeKey): Promise<void> {
    if (locationId === undefined || saving) return;
    const targetLocation = locationId;
    saving = true;
    errorLine = "";
    render();
    const result = await putIdVerificationSetting(
      deps.apiBaseUrl(),
      deps.tenantId(),
      targetLocation,
      {
        mode: next,
      },
    );
    if (!deps.isCurrent()) return;
    saving = false;
    if (result.status !== 200) {
      // Nothing was saved: say why and show what the server holds.
      const message = messageOf(result.body);
      await load();
      errorLine = message;
      render();
      return;
    }
    // Draw what the server holds, not what was tapped.
    mode = result.body.mode;
    render();
  }

  return {
    async refresh() {
      errorLine = "";
      await load();
    },
    reset,
  };
}
